import axios from "axios";
import { API_URL } from "../constants";

export interface RoleRule {
  id?: string;
  ruleText: string;
  order: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface DocumentSigner {
  id: string;
  name: string;
  position?: string | null;
  signatureStatus: "pending" | "signed" | null;
  /** Signed, including a legacy Dokobit signature whose status was never set to "signed". */
  collected?: boolean;
  signedAt?: string | null;
  /** Signatures record id — used to download the exact PDF version this user signed.
   *  Null on an open document when the worker has no signature request row yet. */
  signatureId?: string | null;
  /** Storage id of the PDF version captured when this user signed (Click signatures) */
  fileVersionId?: string | null;
}

export interface RoleDocument {
  id: string;
  role: string;
  file: any;
  createdAt: string;
  /** When true the document is closed / no longer actual (no new signatures requested) */
  isClosed?: boolean;
  /** Per-document signer list with each assigned worker's status for this document */
  signers?: DocumentSigner[];
  signedCount?: number;
  totalCount?: number;
}

export interface AssignedUser {
  id: string;
  name: string;
  email: string;
  position?: string | null;
  signatureStatus?: "pending" | "signed" | null;
  signedAt?: string | null;
}

export interface WorkSecurityGroup {
  id: string;
  name: string;
  /** Number of active (non-archived) roles in the group — returned by fetch-groups */
  rolesCount?: number;
}

export interface WorkSecurityRole {
  id: string;
  name: string;
  description: string;
  company: string;
  /**
   * Instruction area (sphere) the role belongs to — raw id, unpopulated
   * (PR-2 §P1). Present on both the legacy and paginated fetch-roles
   * responses; used to render the "Sritis" column in "all areas" mode.
   */
  area?: string | null;
  /** Group the role belongs to (null / absent = ungrouped) */
  group?: WorkSecurityGroup | null;
  rules: RoleRule[];
  assignedUsers?: AssignedUser[];
  /** Returned by the roles list endpoint (fetch-roles) */
  assignedUsersCount?: number;
  /** Number of generated documents — returned by the roles list endpoint (fetch-roles) */
  documentsCount?: number;
  documents?: RoleDocument[];
  /** True when generation settings (template, series, type, category) are complete */
  isGenerationConfigured?: boolean;
  /** Parent upload category configured in Settings (selection includes its children) */
  uploadCategoryId?: string | null;
  /** All configured upload category roots (includes uploadCategoryId when set) */
  uploadCategoryIds?: string[];
  /** True when an upload category is configured for selecting existing documents */
  isUploadConfigured?: boolean;
  /** File IDs already attached to any work security role in this company */
  attachedWorkSecurityFileIds?: string[];
  /** Archived (retired) roles are hidden from the list and cannot receive new work */
  isArchived?: boolean;
  archivedAt?: number | null;
  /** Whether this role's document must be signed before hiring can complete. */
  requiredAtHiring?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Normalize upload category ids from API / company payloads. */
export function resolveWorkSecurityUploadCategoryIds(source?: {
  uploadCategoryIds?: Array<string | null | undefined> | null;
  uploadCategoryId?: string | null;
  workSecurityRoleUploadCategories?: Array<string | null | undefined> | null;
  workSecurityRoleUploadCategory?: string | null;
  workSecurityRoleUploadCategoryId?: string | null;
} | null): string[] {
  if (!source) return [];
  const ids = new Set<string>();
  for (const id of source.uploadCategoryIds || []) {
    if (id) ids.add(String(id));
  }
  for (const id of source.workSecurityRoleUploadCategories || []) {
    if (id) ids.add(String(id));
  }
  const legacy =
    source.workSecurityRoleUploadCategory ||
    source.workSecurityRoleUploadCategoryId ||
    source.uploadCategoryId;
  if (legacy) ids.add(String(legacy));
  return Array.from(ids);
}

export type WorkSecurityListPagination = {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type WorkSecurityRolesFetchParams = {
  page?: number;
  pageSize?: number;
  textSearch?: string;
  assignmentFilter?: "" | "with_users" | "without_users";
  /** "none" = ungrouped roles only; a group id = that group only; "" / absent = all */
  groupFilter?: string;
  /** "only" = archived roles only (Archive tab); "all" = both; default = active only */
  archived?: "only" | "all";
  /**
   * Instruction area to scope the list to (PR-2 §P1 hard rule): pass this
   * ONLY when there is more than one accessible area and a concrete one is
   * selected — omit the field entirely otherwise, so a single-area company's
   * request stays byte-for-byte the one from before areas existed.
   */
  areaId?: string;
};

export type WorkSecurityRolesFetchResult = {
  entries: WorkSecurityRole[];
  pagination: WorkSecurityListPagination;
};

/** Full roles list — same response as before pagination (backward compatible). */
export async function fetchWorkSecurityRoles(
  companyId: string
): Promise<WorkSecurityRole[]>;
export async function fetchWorkSecurityRoles(
  companyId: string,
  params: WorkSecurityRolesFetchParams & { page: number }
): Promise<WorkSecurityRolesFetchResult>;
export async function fetchWorkSecurityRoles(
  companyId: string,
  params?: WorkSecurityRolesFetchParams
): Promise<WorkSecurityRole[] | WorkSecurityRolesFetchResult> {
  const query = new URLSearchParams();
  const usePagination = params?.page !== undefined;

  if (usePagination) {
    query.set("page", String(params.page));
    if (params.pageSize !== undefined) query.set("pageSize", String(params.pageSize));
    if (params.textSearch?.trim()) query.set("textSearch", params.textSearch.trim());
    if (params.assignmentFilter) query.set("assignmentFilter", params.assignmentFilter);
    if (params.groupFilter) query.set("groupFilter", params.groupFilter);
    if (params.archived) query.set("archived", params.archived);
  }
  // Outside the pagination gate too: the backend honours `areaId` on both
  // response shapes, and the hard rule (omit unless truly narrowing) applies
  // regardless of which shape the caller asked for.
  if (params?.areaId) query.set("areaId", params.areaId);

  const qs = query.toString();
  const res = await axios.get(
    `${API_URL}/${companyId}/work-security/roles${qs ? `?${qs}` : ""}`
  );
  return res.data;
}

export async function fetchWorkSecurityRoleById(
  companyId: string,
  roleId: string
): Promise<WorkSecurityRole> {
  const res = await axios.get(`${API_URL}/${companyId}/work-security/roles/${roleId}`);
  return res.data;
}

export async function createWorkSecurityRole(
  companyId: string,
  data: {
    name: string;
    description: string;
    rules: { ruleText: string; order: number }[];
    /** Group id to place the role into (optional) */
    group?: string | null;
    /** Instruction sphere id (optional — defaults to the company's default sphere). */
    area?: string;
    /** Whether the role's document must be signed before hiring can complete (defaults to true). */
    requiredAtHiring?: boolean;
  }
): Promise<{ role: WorkSecurityRole; file: any }> {
  const res = await axios.post(`${API_URL}/${companyId}/work-security/roles`, data);
  return res.data;
}

export async function amendWorkSecurityRole(
  companyId: string,
  roleId: string,
  data: { rules: { ruleText: string; order: number }[] }
): Promise<{ role: WorkSecurityRole; file: any; signaturesCreated: number }> {
  const res = await axios.put(`${API_URL}/${companyId}/work-security/roles/${roleId}/amend`, data);
  return res.data;
}

/**
 * Update a role's name, description, group, sphere and/or requiredAtHiring
 * flag (metadata only — rules use amend). Changing `area` to a different
 * sphere than the role's current one is Master-only on the backend
 * (COMPANY_USER_MASTER_NOT_FOUND otherwise) — see resolveAreaErrorMessage.
 */
export async function updateWorkSecurityRole(
  companyId: string,
  roleId: string,
  data: {
    name?: string;
    description?: string;
    group?: string | null;
    area?: string;
    requiredAtHiring?: boolean;
  }
): Promise<{ role: WorkSecurityRole }> {
  const res = await axios.patch(
    `${API_URL}/${companyId}/work-security/roles/${roleId}`,
    data
  );
  return res.data;
}

/**
 * Role groups — named per-company groups the roles list is organized by.
 * `areaId` follows the same PR-2 §P1 hard rule as fetchWorkSecurityRoles:
 * omit it entirely unless a concrete area is selected among several.
 */
export async function fetchWorkSecurityGroups(
  companyId: string,
  opts?: { areaId?: string }
): Promise<{ groups: WorkSecurityGroup[] }> {
  const qs = opts?.areaId ? `?areaId=${encodeURIComponent(opts.areaId)}` : "";
  const res = await axios.get(`${API_URL}/${companyId}/work-security/groups${qs}`);
  return res.data;
}

export async function createWorkSecurityGroup(
  companyId: string,
  data: { name: string; area?: string }
): Promise<{ group: WorkSecurityGroup }> {
  const res = await axios.post(`${API_URL}/${companyId}/work-security/groups`, data);
  return res.data;
}

export async function updateWorkSecurityGroup(
  companyId: string,
  groupId: string,
  data: { name: string }
): Promise<{ group: WorkSecurityGroup }> {
  const res = await axios.patch(
    `${API_URL}/${companyId}/work-security/groups/${groupId}`,
    data
  );
  return res.data;
}

/** Deletes a group; its roles are kept and become ungrouped. */
export async function deleteWorkSecurityGroup(
  companyId: string,
  groupId: string
): Promise<{ id: string }> {
  const res = await axios.delete(`${API_URL}/${companyId}/work-security/groups/${groupId}`);
  return res.data;
}

/** Archive (retire) or restore a role. */
export async function setWorkSecurityRoleArchived(
  companyId: string,
  roleId: string,
  isArchived: boolean
): Promise<{ id: string; isArchived: boolean }> {
  const res = await axios.put(
    `${API_URL}/${companyId}/work-security/roles/${roleId}/archive`,
    { isArchived }
  );
  return res.data;
}

export async function assignUsersToRole(
  companyId: string,
  roleId: string,
  userIds: string[]
): Promise<any> {
  const res = await axios.post(`${API_URL}/${companyId}/work-security/roles/${roleId}/assign`, {
    userIds,
  });
  return res.data;
}

export async function unassignUserFromRole(
  companyId: string,
  roleId: string,
  userId: string
): Promise<any> {
  const res = await axios.delete(`${API_URL}/${companyId}/work-security/roles/${roleId}/assign`, {
    data: { userId },
  });
  return res.data;
}

export async function updateWorkSecuritySettings(
  companyId: string,
  data: {
    workSecurityRoleTemplateSource?: "system" | "custom";
    workSecurityRoleTemplate?: string;
    workSecurityRoleSeries?: string;
    workSecurityRoleFileType?: string;
    workSecurityRoleCategory?: string;
    workSecurityRoleUploadCategory?: string;
    workSecurityRoleUploadCategories?: string[];
    // Incident-act slot (v1). Persisted only in CompanyTemplateConfig.
    workSecurityIncidentActTemplateSource?: "system" | "custom";
    workSecurityIncidentActTemplate?: string;
    workSecurityIncidentActSeries?: string;
    workSecurityIncidentActFileType?: string;
    workSecurityIncidentActCategory?: string;
    workSecurityAiQuestionsEnabled?: boolean;
    /**
     * Optional instruction area (sphere) this save applies to (PR-2 §P5).
     * Omitted — the byte-for-byte original request — writes the company-wide
     * ("Darbų sauga") config. NOTE: the backend always upserts the role-slot
     * row for whichever `areaId` is given, even when its fields are left at
     * their currently-displayed (possibly inherited) values — there is no
     * partial/no-op save. Only attach this once the settings screen is
     * actually scoped to a concrete sphere.
     */
    areaId?: string;
  }
): Promise<any> {
  const res = await axios.patch(`${API_URL}/${companyId}/work-security-settings`, data);
  return res.data;
}

/**
 * Master only. Deletes the area's own document settings so it uses the
 * company-wide ones again. Safe to repeat: an area without its own settings
 * answers 200 with removed: 0.
 */
export async function resetWorkSecurityAreaSettings(
  companyId: string,
  areaId: string
): Promise<{ ok: true; areaId: string; removed: number }> {
  const res = await axios.delete(`${API_URL}/${companyId}/work-security-settings/areas/${areaId}`);
  return res.data;
}

export async function generateWorkSecurityDocument(
  companyId: string,
  roleId: string
): Promise<{ file: any; signaturesCreated: number }> {
  const res = await axios.post(
    `${API_URL}/${companyId}/work-security/roles/${roleId}/generate-document`
  );
  return res.data;
}

export async function setWorkSecurityDocumentStatus(
  companyId: string,
  roleId: string,
  documentId: string,
  isClosed: boolean
): Promise<any> {
  const res = await axios.put(
    `${API_URL}/${companyId}/work-security/roles/${roleId}/documents/${documentId}/status`,
    { isClosed }
  );
  return res.data;
}

/** Emails one signer of a role document again; the server picks a reminder or a fresh invitation. */
export async function resendWorkSecurityDocument(
  companyId: string,
  roleId: string,
  documentId: string,
  userId: string
): Promise<{ sent: true }> {
  const res = await axios.post(
    `${API_URL}/${companyId}/work-security/roles/${roleId}/documents/${documentId}/resend`,
    { userId }
  );
  return res.data;
}

export async function associateFileWithWorkSecurityRole(
  companyId: string,
  roleId: string,
  fileId: string
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/work-security/roles/${roleId}/associate-file`,
    { fileId }
  );
  return res.data;
}

export interface AssociatePreviewPerson {
  userId: string;
  name: string;
  email?: string | null;
  status?: string | null;
}

export interface AssociateFilePreview {
  file: { id: string; name: string };
  role: { id: string; name: string };
  existingOnFile: AssociatePreviewPerson[];
  willAdd: AssociatePreviewPerson[];
  alreadyCovered: AssociatePreviewPerson[];
  skippedDisabled: AssociatePreviewPerson[];
  emailsWillBeSent: number;
  assignedUsersCount: number;
}

export async function previewAssociateFileWithWorkSecurityRole(
  companyId: string,
  roleId: string,
  fileId: string
): Promise<AssociateFilePreview> {
  const res = await axios.get(
    `${API_URL}/${companyId}/work-security/roles/${roleId}/associate-file-preview`,
    { params: { fileId } }
  );
  return res.data;
}

export interface UserAssignment {
  id: string;
  name: string;
  email: string;
  position?: string | null;
  assignedRoles: { id: string; name: string }[];
}

export interface AssignableRole {
  id: string;
  name: string;
  /**
   * True when the role's most recent document is closed. With no active document to sign,
   * new workers cannot be added to the role until a document is generated/uploaded/reopened.
   */
  isLatestDocumentClosed?: boolean;
  /**
   * Instruction area (sphere) this role lives in — present so the assignments
   * dashboard can label each role badge when a Master is viewing several
   * areas at once (see api/controllers/work-security/fetch-assignments.js).
   */
  area?: string | null;
}

export interface FetchAssignmentsResponse {
  users: UserAssignment[];
  roles: AssignableRole[];
}

/**
 * NOTE (PR-2 §P3, verified against the shipped controller
 * api/controllers/work-security/fetch-assignments.js): unlike fetch-roles /
 * fetch-groups / incidents-fetch / training-fetch-tests, this endpoint does
 * NOT accept an `areaId` filter — it always scopes to every area the caller
 * can access and returns each role's `area` for the UI to label with
 * (confirmed by the backend's own commit message: "the UI needs to label
 * each role with the one it lives in"). There is therefore no server-side
 * narrowing to wire in here; the assignments panel does the area
 * filtering/labelling client-side using `AssignableRole.area`.
 */
export async function fetchWorkSecurityAssignments(
  companyId: string,
  filters: { textSearch?: string; role?: string }
): Promise<FetchAssignmentsResponse> {
  const res = await axios.post(
    `${API_URL}/${companyId}/work-security/assignments/fetch`,
    { filtersValue: filters }
  );
  return res.data;
}

export async function saveWorkSecurityUserRoles(
  companyId: string,
  userId: string,
  roleIds: string[]
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/work-security/assignments/save`,
    { userId, roleIds }
  );
  return res.data;
}

export interface UserAssignedDocument {
  id: string;
  fileId: string;
  fileName: string;
  createdAt: string;
  signature: {
    id?: string;
    status: "pending" | "signed" | "rejected";
    signedAt?: string | null;
    declineReason?: string | null;
  };
}

export interface UserAssignedRole {
  role: {
    id: string;
    name: string;
    description: string;
  };
  /** Instruction area (sphere) this role belongs to; null on pre-areas data. */
  area?: { id: string; name: string } | null;
  /** Whether this instruction must be signed before hiring can complete (defaults true server-side). */
  requiredAtHiring?: boolean;
  /** All active (non-closed) documents for this role with the user's signature status */
  documents: UserAssignedDocument[];
  /** Assigned by hand (Tvarkos / employee card / approval panel); stays until removed by hand. */
  isIndividual?: boolean;
  /** Brought by one of the employee's positions (positions catalogue). */
  isFromPosition?: boolean;
  /** Names of the positions that bring this role, when `isFromPosition`. */
  fromPositions?: string[];
}

export interface FetchUserAssignedRolesResponse {
  roles: UserAssignedRole[];
  /** Total number of active documents the user has not signed yet (across all roles) */
  unsignedCount: number;
}

export async function fetchUserAssignedRoles(
  companyId: string,
  userId: string
): Promise<FetchUserAssignedRolesResponse> {
  const res = await axios.get(
    `${API_URL}/${companyId}/work-security/users/${userId}/assigned-roles`
  );
  return res.data;
}

export interface UserSignedDocument {
  id: string;
  roleId: string;
  roleName: string;
  /**
   * Instruction area (sphere) the source role lives in — present so the
   * signed-documents tab can label/filter rows when a Master is viewing
   * several areas at once.
   */
  areaId?: string | null;
  fileId: string;
  fileName: string;
  createdAt: string;
  signedAt?: string | null;
  signatureId: string;
  fileVersionId?: string | null;
  isClosed?: boolean;
}

export interface FetchUserSignedDocumentsResponse {
  documents: UserSignedDocument[];
}

/**
 * NOTE (PR-2 §P3, verified against the shipped controller
 * api/controllers/work-security/fetch-user-signed-documents.js): like
 * fetch-assignments, this endpoint does NOT accept an `areaId` filter — it
 * always returns every document across the caller's accessible areas, each
 * tagged with `areaId`. The signed-documents panel narrows by area
 * client-side using that field.
 */
export async function fetchUserSignedDocuments(
  companyId: string,
  userId: string
): Promise<FetchUserSignedDocumentsResponse> {
  const res = await axios.get(
    `${API_URL}/${companyId}/work-security/users/${userId}/signed-documents`
  );
  return res.data;
}
